import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, Download, Clock } from "lucide-react";
import { api, formatMoney, tzOffset } from "@/lib/api";
import { printThermal, esc, rs } from "@/lib/thermal";
import { periodLabel } from "@/components/PeriodPicker";

const m = (n) => `₹${formatMoney(n)}`;
const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

function buildItemsHTML(d, period, filters, printedBy) {
  const items = d.items.length
    ? d.items.map((it) => `<tr><td class="qty">${it.qty}×</td><td class="name">${esc(it.name)}</td><td class="amt">${rs(it.revenue)}</td></tr>`).join("")
    : `<tr><td class="muted">No sales</td></tr>`;
  const hours = d.hourly.filter((h) => h.orders > 0)
    .map((h) => `<tr><td class="name">${hourLabel(h.hour)}–${hourLabel((h.hour + 1) % 24)}</td><td class="qty">${h.qty}</td><td class="amt">${rs(h.revenue)}</td></tr>`).join("");
  return `<h1>TEABOY</h1>
<div class="tag">${esc(d.outlet_name)}</div>
<div class="title">ITEM-WISE REPORT</div>
<div class="meta">Period: <b>${periodLabel(period)}</b><br/>Shift: <b>${esc(filters.shiftText)}</b><br/>Cashier: <b>${esc(filters.cashierText)}</b><br/>Printed: ${new Date().toLocaleString()}</div>
<hr/><table>${items}</table>
<hr/><table><tr><td class="name">Orders</td><td class="amt">${d.order_count}</td></tr><tr><td class="name">Items sold</td><td class="amt">${d.items_sold}</td></tr><tr class="total"><td class="name">TOTAL</td><td class="amt">${rs(d.revenue)}</td></tr></table>
${hours ? `<hr/><h2>Hourly (qty / sales)</h2><table>${hours}</table>` : ""}
<hr/><div class="foot">${printedBy ? "Printed by " + esc(printedBy) : ""}</div>
<div class="tagline">chai for everyone</div>`;
}

export default function ItemwiseReport({ outlet, period, printedBy }) {
  const [shift, setShift] = useState("");
  const [cashier, setCashier] = useState("");
  const [d, setD] = useState(null);

  useEffect(() => setCashier(""), [period, outlet]);

  useEffect(() => {
    let live = true;
    const q = new URLSearchParams({ outlet_id: outlet.id, date_from: period.from, date_to: period.to, tz_offset: tzOffset() });
    if (shift) q.set("shift", shift);
    if (cashier) q.set("cashier_id", cashier);
    api.get(`/reports/items?${q}`)
      .then(({ data }) => live && setD(data))
      .catch((e) => {
        if (!live) return;
        toast.error(e.response?.data?.detail || "Could not load item-wise report");
        if (shift) setShift("");
      });
    return () => { live = false; };
  }, [outlet, period, shift, cashier]);

  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  const shiftDef = d.shifts.find((s) => s.name === shift);
  const filters = {
    shiftText: shiftDef ? `${shiftDef.name} (${shiftDef.start}–${shiftDef.end})` : "All shifts",
    cashierText: d.cashier_options.find((c) => c.id === cashier)?.name || "All cashiers",
  };

  const exportCsv = () => {
    const rows = [["Item", "Qty", "Amount"], ...d.items.map((i) => [i.name, i.qty, i.revenue.toFixed(2)]), ["TOTAL", d.items_sold, d.revenue.toFixed(2)]];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `teaboy-items-${period.from}_${period.to}${shift ? `-${shift}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-6" data-testid="itemwise-report">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="itemwise-shift-select" value={shift} onChange={(e) => setShift(e.target.value)}
            className="bg-card border border-border px-3 py-2.5 text-sm font-semibold">
            <option value="">All shifts</option>
            {d.shifts.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.start}–{s.end})</option>)}
          </select>
          <select data-testid="itemwise-cashier-select" value={cashier} onChange={(e) => setCashier(e.target.value)}
            className="bg-card border border-border px-3 py-2.5 text-sm font-semibold">
            <option value="">All cashiers</option>
            {d.cashier_options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="itemwise-csv-btn" onClick={exportCsv}
            className="flex items-center gap-2 border border-border bg-card px-3.5 py-2.5 text-sm font-semibold hover:bg-muted active:scale-[0.98] transition-transform">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button data-testid="itemwise-print-btn" onClick={() => printThermal("Item-wise report", buildItemsHTML(d, period, filters, printedBy))}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-transform">
            <Printer className="w-4 h-4" /> Print item-wise
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Kpi testid="itemwise-orders" label="Orders" value={d.order_count} />
        <Kpi testid="itemwise-qty" label="Items sold" value={d.items_sold} />
        <Kpi testid="itemwise-revenue" label="Sales" value={m(d.revenue)} />
      </div>

      <ShiftCards shifts={d.shifts} active={shift} onPick={(n) => setShift(n === shift ? "" : n)} />
      <ItemsTable items={d.items} total={d.revenue} />
      <HourlyChart hourly={d.hourly} />
      {d.cashiers.length > 0 && <CashierList cashiers={d.cashiers} />}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-card border border-border p-5" data-testid={testid}>
      <div className="text-sm font-semibold text-muted-foreground">{label}</div>
      <div className="text-3xl font-extrabold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function ShiftCards({ shifts, active, onPick }) {
  return (
    <section>
      <h2 className="text-lg font-bold mb-3">By shift</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {shifts.map((s) => (
          <button key={s.name} data-testid={`shift-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onPick(s.name)}
            className={`text-left p-4 border transition-colors ${active === s.name ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary"}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold">{s.name}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{s.start}–{s.end}</span>
            </div>
            <div className="text-2xl font-extrabold mt-2 tabular-nums">{m(s.revenue)}</div>
            <div className="text-sm text-muted-foreground">{s.orders} orders · {s.qty} items</div>
          </button>
        ))}
      </div>
    </section>
  );
}

function ItemsTable({ items, total }) {
  return (
    <section>
      <h2 className="text-lg font-bold mb-3">Item-wise sales <span className="text-muted-foreground font-semibold text-base">({items.length} items)</span></h2>
      {items.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-sm text-muted-foreground text-center">No sales for this selection.</div>
      ) : (
        <div className="bg-card border border-border" data-testid="itemwise-table">
          <div className="grid grid-cols-[2rem_1fr_5rem_6rem_4rem] gap-3 px-5 py-2.5 text-xs font-bold text-muted-foreground uppercase border-b border-border">
            <span>#</span><span>Item</span><span className="text-right">Qty</span><span className="text-right">Amount</span><span className="text-right">Share</span>
          </div>
          {items.map((it, i) => (
            <div key={it.name} className="grid grid-cols-[2rem_1fr_5rem_6rem_4rem] gap-3 px-5 py-2.5 border-b border-border last:border-b-0 items-center" data-testid={`itemwise-row-${i}`}>
              <span className="text-sm font-bold text-muted-foreground">{i + 1}</span>
              <span className="font-semibold truncate">{it.name}</span>
              <span className="text-right font-bold tabular-nums">{it.qty}</span>
              <span className="text-right font-bold tabular-nums">{m(it.revenue)}</span>
              <span className="text-right text-sm text-muted-foreground tabular-nums">{total ? Math.round((it.revenue / total) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function HourlyChart({ hourly }) {
  const max = Math.max(1, ...hourly.map((h) => h.revenue));
  return (
    <section className="bg-card border border-border p-5" data-testid="hourly-chart">
      <h2 className="text-lg font-bold mb-4">Hourly sales</h2>
      <div className="h-40 flex items-end gap-1">
        {hourly.map((h) => (
          <div key={h.hour} title={`${hourLabel(h.hour)} · ${h.orders} orders · ${h.qty} items · ${m(h.revenue)}`} className="flex-1 h-full flex flex-col justify-end group">
            <div className={`w-full ${h.revenue > 0 ? "bg-primary" : "bg-muted"} group-hover:opacity-75 transition-opacity`}
              style={{ height: `${h.revenue > 0 ? Math.max(4, (h.revenue / max) * 100) : 2}%` }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mt-2">
        {hourly.map((h) => (
          <div key={h.hour} className="flex-1 text-center text-[11px] font-semibold text-muted-foreground">{h.hour % 3 === 0 ? h.hour : ""}</div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hourly-list">
        {hourly.filter((h) => h.orders > 0).map((h) => (
          <div key={h.hour} className="bg-background border border-border px-3 py-2 text-sm">
            <div className="font-bold">{hourLabel(h.hour)}–{hourLabel((h.hour + 1) % 24)}</div>
            <div className="text-muted-foreground">{h.qty} items · <b className="text-foreground">{m(h.revenue)}</b></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CashierList({ cashiers }) {
  return (
    <section>
      <h2 className="text-lg font-bold mb-3">By cashier</h2>
      <div className="bg-card border border-border divide-y divide-border" data-testid="itemwise-cashiers">
        {cashiers.map((c) => (
          <div key={c.id} className="px-5 py-3 flex items-center justify-between">
            <span className="font-semibold">{c.name}</span>
            <span className="text-sm text-muted-foreground">{c.orders} orders · <b className="text-foreground text-base">{m(c.revenue)}</b></span>
          </div>
        ))}
      </div>
    </section>
  );
}
